import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import {
  IonPage,
  IonContent,
  IonInput,
  IonButton,
  IonItem,
  IonText,
  IonIcon,
} from '@ionic/react';
import { fingerPrintOutline } from 'ionicons/icons';
import { useAuth } from '../../application/use-cases/auth/AuthContext';
import { takePendingShareToken } from '../../infrastructure/browser/pendingShare';
import { canUseFingerprintLogin } from '../../infrastructure/browser/webauthnSupport';
import BrandMark from '../components/BrandMark';
import PasswordInput from '../components/PasswordInput';
import './Login.css';

type Mode = 'login' | 'register';

const Login: React.FC = () => {
  const { login, register, loginWithPasskey, isAuthenticated } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fingerprintAvailable, setFingerprintAvailable] = useState(false);
  const [fingerprintBusy, setFingerprintBusy] = useState(false);

  useEffect(() => {
    canUseFingerprintLogin().then(setFingerprintAvailable);
  }, []);

  if (isAuthenticated) {
    // Si llegamos acá después de escanear/tocar una invitación sin sesión
    // (JoinDevice guardó el token antes de mandarnos a /login), la
    // retomamos en vez de ir al dashboard.
    const pendingToken = takePendingShareToken();
    return <Redirect to={pendingToken ? `/join?token=${encodeURIComponent(pendingToken)}` : '/app/dashboard'} />;
  }

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    const result = mode === 'login' ? await login(email, password) : await register(email, password, name);
    setLoading(false);
    if (!result.ok) {
      setError(result.error ?? 'Ocurrió un error.');
    }
  };

  const toggleMode = () => {
    setMode((m) => (m === 'login' ? 'register' : 'login'));
    setError(null);
  };

  const handleFingerprintLogin = async () => {
    setError(null);
    setFingerprintBusy(true);
    const result = await loginWithPasskey();
    setFingerprintBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'No se pudo ingresar con huella.');
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen className="login-content">
        <div className="login-container">
          <div className="login-logo">
            <BrandMark />
            <h1>DomIoT</h1>
            <p>Clima inteligente para tu campo</p>
          </div>

          {mode === 'register' && (
            <IonItem>
              <IonInput
                placeholder="Nombre"
                value={name}
                onIonInput={(e) => setName(e.detail.value ?? '')}
              />
            </IonItem>
          )}

          <IonItem>
            <IonInput
              placeholder="Email"
              type="email"
              value={email}
              onIonInput={(e) => setEmail(e.detail.value ?? '')}
            />
          </IonItem>

          <PasswordInput placeholder="Contraseña" value={password} onValueChange={setPassword} />

          {error && (
            <IonText color="danger">
              <p className="login-error">{error}</p>
            </IonText>
          )}

          <IonButton expand="block" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Un momento...' : mode === 'login' ? 'Ingresar' : 'Crear cuenta'}
          </IonButton>

          {mode === 'login' && fingerprintAvailable && (
            <IonButton
              expand="block"
              fill="outline"
              onClick={handleFingerprintLogin}
              disabled={loading || fingerprintBusy}
            >
              <IonIcon icon={fingerPrintOutline} slot="start" />
              {fingerprintBusy ? 'Verificando...' : 'Ingresar con huella'}
            </IonButton>
          )}

          {mode === 'login' && (
            <>
              <IonButton expand="block" fill="clear" routerLink="/forgot-password" disabled={loading}>
                ¿Olvidaste tu contraseña?
              </IonButton>
              <div className="login-divider" />
            </>
          )}

          <IonButton expand="block" fill="clear" onClick={toggleMode} disabled={loading}>
            {mode === 'login' ? '¿No tenés cuenta? Creá una' : '¿Ya tenés cuenta? Ingresá'}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Login;
